import React from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DropResult } from 'react-beautiful-dnd';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { MAX_ITEMS_PER_PAGE } from '../../common/constants/tab-content.tsx';
import type {
	FavouriteItem,
	ReorderFavouritePayload,
	ReorderRequestBody,
	FetchFavouritesData,
} from '../../common/types/favourites.tsx';
import type {
	InjectedItemProviderProps,
	ItemProviderData,
	ItemProviderDataSection,
	ReorderItemsResult,
	UpdateData,
} from '../../common/types/item-provider.tsx';
import type { Item } from '../../common/types/item.tsx';
import ItemProvider from '../item-provider/index.tsx';

export const queryUrl = `/rest/internal/2/favourites?count=${MAX_ITEMS_PER_PAGE}&types=projects,issues,filters,dashboards,boards,queues,plans`;

export const transformResult = (data?: FavouriteItem[]): ItemProviderData | undefined => {
	if (typeof data === 'undefined' || Array.isArray(data) === false) {
		return undefined;
	}
	if (data.length === 0) {
		return { sections: [], total: 0 };
	}
	return {
		sections: [
			{
				items: data.map((item: FavouriteItem) => {
					const mappedItem: Item = { ...item, type: `${item.type}s`, id: `${item.id}` };

					if (item.type === 'board' && typeof item.metadata === 'string') {
						mappedItem.attributes = mappedItem.attributes ? mappedItem.attributes : {};
						mappedItem.attributes = {
							...mappedItem.attributes,
							containerName: item.metadata,
						};
					}
					mappedItem.entityId = item.id;

					return mappedItem;
				}),
			},
		],
		total: data.length,
	};
};

export const fetchData: FetchFavouritesData = async (): Promise<FavouriteItem[]> => {
	const result = await fetchJson(queryUrl, {});

	if (typeof result === 'undefined' || !Array.isArray(result)) {
		throw Error('Could not retrieve your favourites');
	}
	return result;
};

const mapToRequest = (payload: ReorderFavouritePayload): ReorderRequestBody => {
	const { entity, beforeEntity } = payload;

	return {
		entity: {
			id: Number(entity.id),
			type: entity.type,
		},
		beforeEntityPosition: beforeEntity
			? {
					id: Number(beforeEntity.id),
					type: beforeEntity.type,
				}
			: null,
	};
};

export const reorderData = async (payload: ReorderFavouritePayload) => {
	// TODO do the backend call
	const url = '/rest/internal/2/favourites';
	const body = mapToRequest(payload);

	return performPostRequest(url, { body: JSON.stringify(body) })
		.then(() => true)
		.catch(() => false);
};

const reorder = <T,>(list: T[], fromIndex: number, toIndex: number): T[] => {
	const result = [...list];
	const [removedItem] = result.splice(fromIndex, 1);
	result.splice(toIndex, 0, removedItem);

	return result;
};

export const onReorderFavouriteItems = (
	result: DropResult,
	sections: ItemProviderDataSection[],
): ReorderItemsResult | undefined => {
	if (!sections || sections.length < 1) return undefined;

	const starredGroup = sections[0].items;
	const { destination } = result;

	if (!destination || result.source.index === destination.index) {
		return undefined;
	}

	const starredItemsReordered = reorder(starredGroup, result.source.index, destination.index);
	const draggedEntity = starredItemsReordered[destination.index];
	const beforeEntity = starredItemsReordered[destination.index + 1];

	const reorderItems = { items: starredItemsReordered };

	return {
		optimisticUpdate: { sections: [reorderItems] },
		save: () =>
			reorderData({
				entity: draggedEntity,
				beforeEntity,
				starredItemsReordered,
			}),
	};
};

export const onReorderFavouriteItemsNew = (
	result: DropResult,
	sections: ItemProviderDataSection[],
	updateData?: UpdateData,
) => {
	if (!sections || sections.length < 1) return undefined;

	const starredGroup = sections[0].items;
	const { destination } = result;

	if (!updateData || !destination || result.source.index === destination.index) {
		return undefined;
	}

	updateData((data) => {
		if (Array.isArray(data) && data.length > 1 && data[0].id && data[0].type) {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const favourites = data as FavouriteItem[];
			return reorder(favourites, result.source.index, destination.index);
		}
		return data;
	});

	const starredItemsReordered = reorder<Item>(starredGroup, result.source.index, destination.index);
	const draggedEntity = starredItemsReordered[destination.index];
	const beforeEntity = starredItemsReordered[destination.index + 1];

	const reorderItems = { items: starredItemsReordered };

	return {
		optimisticUpdate: { sections: [reorderItems] },
		save: () =>
			reorderData({
				entity: draggedEntity,
				beforeEntity,
				starredItemsReordered,
			}),
	};
};

const FavouritesProvider = (props: InjectedItemProviderProps): React.JSX.Element => {
	const { userProviderData, ...otherProps } = props;
	return (
		<ItemProvider
			{...otherProps}
			onReorderItems={onReorderFavouriteItems}
			onReorderItemsNew={onReorderFavouriteItemsNew}
			autoUpdate
		/>
	);
};

export default FavouritesProvider;
